<!--
  LinkedEntitiesList — Phase 4-6 / T31.

  Bottom-of-panel list of host entities the operator (or auto-match) linked to
  this conversation. Lives inside CustomerContextPanel; both matched and
  unmatched states render this footer.

  Visual direction (impeccable T31):
    - Section header "Linked entities" in uppercase micro-caps.
    - Each row: ALL-CAPS entity-type label tinted by a deterministic OKLCH hue
      + short id in monospace + relative created-time below.
    - Right edge: "Open ↗" ghost-text button (accent color). Disabled when the
      adapter returned no deepLink.
    - Newest first by createdAt. Cap at 8 visible; "Show all (N)" expands the
      remainder. Empty state is one muted line.
-->
<script lang="ts">
  import type { HostLinkedEntity } from '../lib/api/queries.js';
  import { postOpenHostEntity } from '../lib/embed/bridge-singleton.svelte.js';

  type Props = {
    linkedEntities: HostLinkedEntity[];
  };
  let { linkedEntities = [] }: Props = $props();

  const sorted = $derived(
    [...linkedEntities].sort((a, b) => {
      const ta = new Date(a.createdAt).getTime();
      const tb = new Date(b.createdAt).getTime();
      return (Number.isNaN(tb) ? 0 : tb) - (Number.isNaN(ta) ? 0 : ta);
    }),
  );

  const COLLAPSED_LIMIT = 8;
  let expanded = $state(false);
  const overflow = $derived(sorted.length > COLLAPSED_LIMIT);
  const visible = $derived(expanded ? sorted : sorted.slice(0, COLLAPSED_LIMIT));

  function hashHue(input: string): number {
    let h = 0;
    for (let i = 0; i < input.length; i++) h = (h * 31 + input.charCodeAt(i)) | 0;
    return Math.abs(h) % 360;
  }

  function shortId(id: string): string {
    if (id.length <= 10) return id;
    return '…' + id.slice(-6);
  }

  function formatRelative(iso: string): string {
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return iso;
    const diff = Math.max(0, Date.now() - then);
    const min = 60_000;
    const hr = 60 * min;
    const day = 24 * hr;
    if (diff < min) return 'just now';
    if (diff < hr) return `${Math.floor(diff / min)}m ago`;
    if (diff < day) return `${Math.floor(diff / hr)}h ago`;
    if (diff < 7 * day) return `${Math.floor(diff / day)}d ago`;
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }

  function openEntity(entity: HostLinkedEntity) {
    if (!entity.deepLink) return;
    postOpenHostEntity(entity.entityType, entity.entityId);
  }

  function toggleExpand() {
    expanded = !expanded;
  }
</script>

<section class="entities" aria-label="Linked entities">
  <h3 class="section-h">Linked entities</h3>

  {#if sorted.length === 0}
    <p class="empty">Nothing created yet.</p>
  {:else}
    <ul class="rows" role="list">
      {#each visible as entity (entity.entityType + ':' + entity.entityId)}
        {@const hue = hashHue(entity.entityType)}
        <li class="row">
          <div class="row-body">
            <div class="row-top">
              <span
                class="type"
                style="--type-fg: oklch(48% 0.16 {hue})"
              >{entity.entityType}</span>
              <span class="dot" aria-hidden="true">·</span>
              <span class="id" title={entity.entityId}>{shortId(entity.entityId)}</span>
            </div>
            <div class="row-time">{formatRelative(entity.createdAt)}</div>
          </div>
          <button
            type="button"
            class="open"
            disabled={!entity.deepLink}
            aria-label="Open {entity.entityType} {shortId(entity.entityId)} in host"
            onclick={() => openEntity(entity)}
          >
            Open<span class="glyph" aria-hidden="true">↗</span>
          </button>
        </li>
      {/each}
    </ul>

    {#if overflow}
      <button type="button" class="show-all" onclick={toggleExpand}>
        {expanded ? 'Show fewer' : `Show all (${sorted.length})`}
      </button>
    {/if}
  {/if}
</section>

<style>
  .entities {
    padding: 14px 16px 18px;
    border-top: 1px solid color-mix(in oklch, var(--widget-border, oklch(92% 0.005 250)) 60%, transparent);
    margin-top: auto;
  }

  .section-h {
    font-size: 10.5px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    font-weight: 700;
    margin: 0 0 10px;
  }

  .empty {
    margin: 0;
    font-size: 12px;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    font-style: italic;
  }

  .rows {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .row {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    gap: 10px;
    padding: 6px 0;
    min-height: 38px;
  }

  .row-body {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  .row-top {
    display: inline-flex;
    align-items: baseline;
    gap: 6px;
    min-width: 0;
  }

  .type {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: var(--type-fg, var(--widget-fg-muted, oklch(52% 0.012 250)));
    white-space: nowrap;
  }

  .dot {
    color: color-mix(in oklch, var(--widget-fg, oklch(20% 0.01 250)) 30%, transparent);
    font-size: 11px;
  }

  .id {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 11.5px;
    color: var(--widget-fg, oklch(20% 0.01 250));
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }

  .row-time {
    font-size: 11px;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    line-height: 1.2;
  }

  .open {
    background: none;
    border: none;
    color: var(--widget-accent, oklch(75% 0.165 75));
    font-size: 11.5px;
    font-weight: 600;
    padding: 4px 6px;
    border-radius: 6px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 3px;
    transition: background 100ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  .open:hover:not(:disabled) {
    text-decoration: underline;
  }
  .open:focus-visible {
    outline: 2px solid var(--widget-accent, oklch(75% 0.165 75));
    outline-offset: 1px;
  }
  .open:disabled {
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    cursor: not-allowed;
    opacity: 0.5;
  }
  .open .glyph {
    font-size: 11px;
    line-height: 1;
  }

  .show-all {
    margin-top: 10px;
    background: none;
    border: none;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    font-size: 11.5px;
    font-weight: 600;
    padding: 4px 0;
    cursor: pointer;
    text-decoration: underline;
    text-underline-offset: 2px;
  }
  .show-all:hover {
    color: var(--widget-fg, oklch(20% 0.01 250));
  }
</style>
